import { clone, deepEquals } from '@jack-henry/frontend-utils/functions';
import { debounce } from '@treasury/utils/functions';
import { add, isValid, startOfMonth, startOfWeek, startOfYear, sub } from 'date-fns';
import { html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { SelectItemVm, TmValueChanged } from '../components/forms';
import { TmInputFieldBase } from './tm-input-field.base';

import './tm-date-picker';
import './tm-select';

export enum DateRangeType {
    None = 'None',
    Today = 'Today',
    SpecificDate = 'Specific Date',
    DateRange = 'Date Range',
    WeekToDate = 'Week to Date',
    MonthToDate = 'Month to Date',
    YearToDate = 'Year to Date',
}

export interface DateRange {
    rangeType: DateRangeType;
    startDate?: Date;
    endDate?: Date;
}

export const tagName = 'tm-date-range';
@customElement(tagName)
export class TmDateRange extends TmInputFieldBase<DateRange> {
    @property({ type: String })
    public label = 'Date Range';

    @property({ type: Boolean, reflect: true })
    public required = false;

    @property({ type: Array })
    public dateRangeOptions: DateRangeType[] = [
        DateRangeType.Today,
        DateRangeType.SpecificDate,
        DateRangeType.DateRange,
        DateRangeType.WeekToDate,
        DateRangeType.MonthToDate,
        DateRangeType.YearToDate,
    ];

    @state()
    private _maxDate?: Date;

    @state()
    private _minDate?: Date;

    @state()
    private rangeType = DateRangeType.None;

    @state()
    private _endDate?: Date;

    @state()
    private _startDate?: Date;

    public get startDate() {
        return this._startDate;
    }

    public set startDate(value: Date | undefined) {
        value = clone(value);
        this._startDate = this.zeroDateHours(value);
    }

    public get endDate() {
        return this._endDate;
    }

    public set endDate(value: Date | undefined) {
        value = clone(value);
        this._endDate = this.zeroDateHours(value);
    }

    public get maxDate() {
        return this._maxDate;
    }

    public set maxDate(value: Date | undefined) {
        this._maxDate = this.zeroDateHours(value);
    }

    public get minDate() {
        return this._minDate;
    }

    public set minDate(value: Date | undefined) {
        this._minDate = this.zeroDateHours(value);
    }

    private get rangeOptionsForSelect() {
        const rangeOptions: SelectItemVm<string>[] = this.dateRangeOptions.map(rangeOption => ({
            label: rangeOption,
            value: rangeOption,
        }));

        if (!this.required) {
            rangeOptions.unshift({ label: '', value: DateRangeType.None });
        }

        return rangeOptions;
    }

    private get isValidDateRange() {
        if (this.rangeType !== DateRangeType.None && !this.startDate) {
            return false;
        }

        switch (this.rangeType) {
            case DateRangeType.SpecificDate:
                return this.startDate && isValid(this.startDate);
            case DateRangeType.DateRange:
                return !!(
                    this.startDate &&
                    isValid(this.startDate) &&
                    this.endDate &&
                    isValid(this.endDate)
                );
            default:
                return true;
        }
    }

    private get startDateReadOnly() {
        return (
            this.rangeType !== DateRangeType.SpecificDate &&
            this.rangeType !== DateRangeType.DateRange
        );
    }

    private get endDateReadOnly() {
        return this.rangeType !== DateRangeType.DateRange;
    }

    private get maxStartDate() {
        if (this.endDate && this.rangeType === DateRangeType.DateRange) {
            return sub(this.endDate, { days: 1 });
        }
        return this.maxDate;
    }

    private get minEndDate() {
        if (this.rangeType !== DateRangeType.DateRange) {
            return undefined;
        }

        if (this.startDate) {
            return add(this.startDate, { days: 1 });
        }

        return this.minDate;
    }

    /**
     * Commit the current range model and notify listeners.
     *
     * This must be a property rather than a method so that it can be used with `debounce()`.
     */
    private readonly dispatchValueChange = debounce(() => {
        const { rangeType, startDate, endDate } = this;
        const dateRange: DateRange = {
            rangeType,
            startDate,
            endDate,
        };

        this.changeValue(dateRange);
    }, 200);

    protected validate() {
        return this.isValidDateRange ?? true;
    }

    protected update(changedProps: Map<PropertyKey, unknown>): void {
        super.update(changedProps);
        const { initialChanged, rangeTypeChangedExclusive, validModelChanged } =
            this.createUpdateDigest(changedProps);

        // if the range is updated, but has the same values as before then don't
        // unpack the individual properties to avoid redundant @selection events
        if (initialChanged) {
            this.unpackRange(this.initial);
        }

        if (rangeTypeChangedExclusive) {
            // if only the range type changed, then update the dates to match
            this.updateDateSelections(this.rangeType);
        }

        if (validModelChanged) {
            // if any of the model properties have changed and the range is valid,
            // then we should commit the value and notify listeners
            this.dispatchValueChange();
        }
    }

    protected render() {
        return [this.renderRangeTypeSelect(), this.renderDatePickers()];
    }

    private createUpdateDigest(changedProps: Map<PropertyKey, unknown>) {
        const lastInitial = changedProps.get('initial') as DateRange | undefined;
        const initialChanged =
            changedProps.has('initial') && !deepEquals(lastInitial, this.initial);
        const rangeTypeChangedExclusive = changedProps.has('rangeType') && changedProps.size === 1;
        const modelChanged =
            changedProps.has('rangeType') ||
            changedProps.has('_startDate') ||
            changedProps.has('_endDate');

        return {
            initialChanged,
            rangeTypeChangedExclusive,
            validModelChanged: modelChanged && this.isValidDateRange,
        };
    }

    private unpackRange(range?: DateRange) {
        if (!range) {
            this.rangeType = DateRangeType.None;
            this.startDate = undefined;
            this.endDate = undefined;
            return;
        }

        const { rangeType, startDate, endDate } = range;
        this.rangeType = rangeType;
        this.startDate = startDate;
        this.endDate = endDate;
    }

    private zeroDateHours(date?: Date) {
        if (!date) {
            return undefined;
        }

        const fns = ['getHours', 'getMinutes', 'getSeconds', 'getMilliseconds'] as const;
        const zeroedDate = fns.every(fn => date[fn]() === 0);
        return zeroedDate ? date : new Date(date.setHours(0, 0, 0, 0));
    }

    private async updateDateSelections(rangeType?: DateRangeType) {
        if (!rangeType) {
            return;
        }

        switch (rangeType) {
            case DateRangeType.Today:
                this.startDate = new Date();
                this.endDate = undefined;
                break;
            case DateRangeType.None:
            case DateRangeType.SpecificDate:
            case DateRangeType.DateRange:
                this.startDate = undefined;
                this.endDate = undefined;
                break;
            case DateRangeType.WeekToDate:
                this.startDate = new Date(startOfWeek(new Date()));
                this.endDate = new Date();
                break;
            case DateRangeType.MonthToDate:
                this.startDate = new Date(startOfMonth(new Date()));
                this.endDate = new Date();
                break;
            case DateRangeType.YearToDate:
                this.startDate = new Date(startOfYear(new Date()));
                this.endDate = new Date();
                break;
            default:
                throw new Error(`Invalid range type: "${rangeType}"`);
        }
    }

    private updateRangeTypeSelection() {
        if (!this.startDate && !this.endDate && !this.required) {
            this.rangeType = DateRangeType.None;
            return;
        }

        if (this.startDate && this.rangeType !== DateRangeType.DateRange) {
            if (this.endDate) {
                this.rangeType = DateRangeType.DateRange;
            } else {
                this.rangeType = DateRangeType.SpecificDate;
            }
        }

        if (this.rangeType === DateRangeType.SpecificDate && isDateToday(this.startDate)) {
            this.rangeType = DateRangeType.Today;
            return;
        }

        if (this.rangeType === DateRangeType.DateRange && isDateToday(this.endDate)) {
            switch (this.startDate?.getTime()) {
                case new Date(startOfWeek(new Date())).setHours(0, 0, 0, 0):
                    this.rangeType = DateRangeType.WeekToDate;
                    break;
                case new Date(startOfMonth(new Date())).setHours(0, 0, 0, 0):
                    this.rangeType = DateRangeType.MonthToDate;
                    break;
                case new Date(startOfYear(new Date())).setHours(0, 0, 0, 0):
                    this.rangeType = DateRangeType.YearToDate;
                    break;
                default:
                    break;
            }
        }
    }

    private renderRangeTypeSelect() {
        return html` <tm-select
            .label=${this.label}
            .items=${this.rangeOptionsForSelect}
            .initial=${this.rangeType ?? DateRangeType.None}
            @value-changed=${(e: TmValueChanged<DateRangeType>) => {
                const { value, isValid } = e.detail;
                if (!value || !isValid || value === this.rangeType) {
                    return;
                }

                this.rangeType = value;
            }}
        ></tm-select>`;
    }

    private renderStartDatePicker() {
        return html` <tm-date-picker
            placeholder="mm/dd/yyyy"
            .min=${this.minDate}
            .max=${this.maxStartDate}
            .initial=${this.startDate}
            @value-changed=${(e: TmValueChanged<Date>) => {
                const { value, isValid } = e.detail;
                if (!isValid || this.startDate === value) {
                    return;
                }

                this.startDate = value;
                this.updateRangeTypeSelection();
            }}
            class="min-w-0 w-full"
            .readonly=${this.startDateReadOnly}
        ></tm-date-picker>`;
    }

    private renderEndDatePicker() {
        if (
            this.rangeType === DateRangeType.SpecificDate ||
            this.rangeType === DateRangeType.Today
        ) {
            return nothing;
        }

        return html` <div class="px-2">-</div>
            <tm-date-picker
                placeholder="mm/dd/yyyy"
                .min=${this.minEndDate}
                .max=${this.maxDate}
                .initial=${this.endDate}
                @value-changed=${(e: TmValueChanged<Date>) => {
                    const { value, isValid } = e.detail;

                    if (!isValid || this.endDate === value) {
                        return;
                    }

                    this.endDate = value;
                    this.updateRangeTypeSelection();
                }}
                .readonly=${this.endDateReadOnly}
                class="min-w-0 w-full"
            ></tm-date-picker>`;
    }

    private renderDatePickers() {
        if (this.rangeType === DateRangeType.None) {
            return nothing;
        }
        return html` <div class="flex items-center">
            ${this.renderStartDatePicker()} ${this.renderEndDatePicker()}
        </div>`;
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: TmDateRange;
    }
}

function isDateToday(date?: Date): boolean {
    if (!date) {
        return false;
    }

    return date.getTime() === new Date().setHours(0, 0, 0, 0);
}
